import React, { useMemo } from 'react'
import { useNewTranslation } from 'hooks/index'
import { useSelector } from 'react-redux'
import { Box } from 'rebass'
import { State } from 'state'
import { TagOption } from 'state/constants'
import { theme } from 'theme'
import { Paper, Popper } from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'

import { SubmissionState } from 'state/submissions'
import { makeCommentTagBadgesSelector } from 'state/review'
import Text from 'particles/Text'
import TextTag from 'particles/Tag/TextTag'
import { TagContentType } from 'particles/Tag'

interface CommentCellProps {
    submission: SubmissionState
    contentType: TagContentType
}

export const useStyles = makeStyles(() =>
    createStyles({
        root: {
            alignItems: 'center',
            lineHeight: '24px',
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
            '& .cellValue': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
    }),
)

interface GridCellExpandProps {
    commentCellProps: CommentCellProps
    width?: number
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const [newT] = useNewTranslation()
    const { width, commentCellProps } = props
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef(null)
    const cellValue = React.useRef(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)
    const selectTagBadges = useMemo(makeCommentTagBadgesSelector, [])
    const tagBadges = useSelector((state: State) =>
        selectTagBadges(state, { submissionProgress: commentCellProps.submission }),
    )

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = commentCellProps.submission.tag_uuids
            ? commentCellProps.submission.tag_uuids.length > 0 || commentCellProps.submission.comment.length > 13
            : false
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    return (
        <div ref={wrapper} className={classes.root} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            <div ref={cellValue} className="cellValue">
                {tagBadges && tagBadges.length > 0 && (
                    <Box sx={{ textAlign: 'left' }}>
                        {tagBadges.map((tag: TagOption) => {
                            return (
                                <TextTag key={tag.uuid} contentType={commentCellProps.contentType} label={tag.label} />
                            )
                        })}
                    </Box>
                )}
                <Text scale="bodyM500" color="primary" style={{ wordBreak: 'break-all' }}>
                    {commentCellProps.submission.comment
                        ? commentCellProps.submission.comment
                        : newT('submission.review.noSubmissionComment', 'None')}
                </Text>
            </div>
            {showPopper && (
                <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
                    <Paper
                        elevation={1}
                        style={{
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.newColors.neutral50,
                            width,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        {tagBadges && tagBadges.length > 0 && (
                            <Box sx={{ textAlign: 'left' }}>
                                {tagBadges.map((tag: TagOption) => {
                                    return (
                                        <TextTag
                                            key={tag.uuid}
                                            contentType={commentCellProps.contentType}
                                            label={tag.label}
                                        />
                                    )
                                })}
                            </Box>
                        )}
                        <Text scale="bodyM500" color="primary">
                            {commentCellProps.submission.comment
                                ? commentCellProps.submission.comment
                                : newT('submission.review.noSubmissionComment', 'None')}
                        </Text>
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const CommentCell = (params: GridRenderCellParams) => {
    const commentCellProps: CommentCellProps = params.value as CommentCellProps
    return <GridCellExpand commentCellProps={commentCellProps} width={params.colDef.width} />
}
